import React, { useRef } from 'react';
import { useFormikContext } from 'formik';
import { Maths } from 'helpers/maths';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import {
  COL_SPAN_ONE_FOURTH,
  COL_SPAN_HALF
} from 'components/FormGrid/constants';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import { GroupedCurrencyPair, ReadOnlyCurrency } from './CurrencyDisplay';
import {
  parseControlledBlurValue,
  parseControlledChangeValue
} from '../helpers/parseFormValue';
import { calculateTotalFixedCost } from '../helpers/fixedCostCalculations';
import {
  calculateYieldTotal,
  calculateCropSharePercentDecimal,
  calculateVariableCostLive,
  calculateProjectedNetRevenue
} from '../helpers/projectedRevenueCalculations';
import { useStableFieldBuffer } from '../helpers/useStableFieldBuffer';
import { PlanWizardFormValues } from '../../../types';

const DOLLAR_PREFIX = <span className="text-neutral-60">$</span>;
const TOTAL_SUFFIX = <span className="text-neutral-60">total</span>;
const PER_AC_SUFFIX = <span className="text-neutral-60">/ac</span>;
const YIELD_PER_ACRE_SUFFIX = <span className="text-neutral-60">units/ac</span>;
const PER_UNIT_SUFFIX = <span className="text-neutral-60">/unit</span>;

interface ProjectedRevenueSectionProps {
  area: number;
  financialFieldsDisabled: boolean;
}

const ProjectedRevenueSection: React.FC<ProjectedRevenueSectionProps> = ({
  area,
  financialFieldsDisabled
}) => {
  const { values, setFieldValue } = useFormikContext<PlanWizardFormValues>();
  const stableFieldObject = useStableFieldBuffer();
  const lastAcceptedCropSharePercent = useRef(
    values.projectedRevenue.cropSharePercent ?? 100
  );

  const totalFixedCost = calculateTotalFixedCost(values.fixedCost);

  const yieldTotal = calculateYieldTotal(
    values.projectedRevenue.yieldPerArea,
    values.projectedRevenue.pricePerUnit,
    area
  );

  const cropSharePercentDecimal = calculateCropSharePercentDecimal(
    values.projectedRevenue.cropSharePercent
  );
  const variableCostLive = calculateVariableCostLive(values.planProductEntries);
  const projectedNetRevenue = calculateProjectedNetRevenue(
    yieldTotal,
    values.projectedRevenue.fsaPayment,
    cropSharePercentDecimal,
    totalFixedCost,
    variableCostLive
  );

  const projectedRevenueRows: FormRowConfig[] = [
    {
      id: 'yield-row',
      fields: [
        {
          id: 'yieldPerArea',
          colSpan: COL_SPAN_ONE_FOURTH,
          render: () => (
            <FormInputWithPrecisionDisplay
              object={stableFieldObject(
                'yieldPerArea',
                values.projectedRevenue.yieldPerArea
              )}
              fieldName="yieldPerArea"
              label="Yield per Acre"
              disabled={financialFieldsDisabled}
              inputSuffix={YIELD_PER_ACRE_SUFFIX}
              allowEmptyDisplay
              onControlledChange={event => {
                const parsed = parseControlledChangeValue(event);
                setFieldValue('projectedRevenue.yieldPerArea', parsed);
                setFieldValue(
                  'projectedRevenue.yieldTotal',
                  calculateYieldTotal(
                    parsed,
                    values.projectedRevenue.pricePerUnit,
                    area
                  )
                );
              }}
              onControlledBlur={newValue => {
                const parsed = parseControlledBlurValue(newValue);
                setFieldValue('projectedRevenue.yieldPerArea', parsed);
                setFieldValue(
                  'projectedRevenue.yieldTotal',
                  calculateYieldTotal(
                    parsed,
                    values.projectedRevenue.pricePerUnit,
                    area
                  )
                );
              }}
            />
          )
        },
        {
          id: 'pricePerUnit',
          colSpan: COL_SPAN_ONE_FOURTH,
          render: () => (
            <FormInputWithPrecisionDisplay
              object={stableFieldObject(
                'pricePerUnit',
                values.projectedRevenue.pricePerUnit
              )}
              fieldName="pricePerUnit"
              label="Sale Price Per Unit"
              inputPrefix={DOLLAR_PREFIX}
              inputSuffix={PER_UNIT_SUFFIX}
              disabled={financialFieldsDisabled}
              allowEmptyDisplay
              onControlledChange={event => {
                const parsed = parseControlledChangeValue(event);
                setFieldValue('projectedRevenue.pricePerUnit', parsed);
                setFieldValue(
                  'projectedRevenue.yieldTotal',
                  calculateYieldTotal(
                    values.projectedRevenue.yieldPerArea,
                    parsed,
                    area
                  )
                );
              }}
              onControlledBlur={newValue => {
                const parsed = parseControlledBlurValue(newValue);
                setFieldValue('projectedRevenue.pricePerUnit', parsed);
                setFieldValue(
                  'projectedRevenue.yieldTotal',
                  calculateYieldTotal(
                    values.projectedRevenue.yieldPerArea,
                    parsed,
                    area
                  )
                );
              }}
            />
          )
        },
        {
          id: 'yieldTotal',
          colSpan: COL_SPAN_ONE_FOURTH,
          render: () => (
            <ReadOnlyCurrency
              label="Yield Revenue"
              value={yieldTotal}
              testId="yieldRevenue-readout"
            />
          )
        }
      ]
    },
    {
      // Divider between the Yield row and this row, per Figma.
      id: 'fsa-crop-share-row',
      rowClassName: 'has-separator',
      fields: [
        {
          // FSA Payment collapses to 1 slot as a grouped pair, so this row
          // uses 3 columns not 4.
          id: 'fsaPayment',
          colSpan: COL_SPAN_HALF,
          render: () => (
            <GroupedCurrencyPair
              label="FSA Payment"
              first={
                <FormInputWithPrecisionDisplay
                  object={stableFieldObject(
                    'fsaPayment',
                    values.projectedRevenue.fsaPayment
                  )}
                  fieldName="fsaPayment"
                  inputPrefix={DOLLAR_PREFIX}
                  inputSuffix={TOTAL_SUFFIX}
                  disabled={financialFieldsDisabled}
                  allowEmptyDisplay
                  onControlledChange={event => {
                    const parsed = parseControlledChangeValue(event);
                    setFieldValue('projectedRevenue.fsaPayment', parsed);
                    setFieldValue(
                      'projectedRevenue.fsaPaymentPerAc',
                      parsed === null
                        ? null
                        : Maths.divideSafeRound(parsed, area, 2)
                    );
                  }}
                  onControlledBlur={newTotal => {
                    const parsed = parseControlledBlurValue(newTotal);
                    setFieldValue('projectedRevenue.fsaPayment', parsed);
                    setFieldValue(
                      'projectedRevenue.fsaPaymentPerAc',
                      parsed === null
                        ? null
                        : Maths.divideSafeRound(parsed, area, 2)
                    );
                  }}
                />
              }
              second={
                <FormInputWithPrecisionDisplay
                  object={stableFieldObject(
                    'fsaPaymentPerAc',
                    values.projectedRevenue.fsaPaymentPerAc
                  )}
                  fieldName="fsaPaymentPerAc"
                  inputPrefix={DOLLAR_PREFIX}
                  inputSuffix={PER_AC_SUFFIX}
                  disabled={financialFieldsDisabled}
                  allowEmptyDisplay
                  onControlledChange={event => {
                    const parsed = parseControlledChangeValue(event);
                    setFieldValue('projectedRevenue.fsaPaymentPerAc', parsed);
                    setFieldValue(
                      'projectedRevenue.fsaPayment',
                      parsed === null
                        ? null
                        : Maths.multiplyRound(parsed, area, 2)
                    );
                  }}
                  onControlledBlur={newPerAc => {
                    const parsed = parseControlledBlurValue(newPerAc);
                    setFieldValue('projectedRevenue.fsaPaymentPerAc', parsed);
                    setFieldValue(
                      'projectedRevenue.fsaPayment',
                      parsed === null
                        ? null
                        : Maths.multiplyRound(parsed, area, 2)
                    );
                  }}
                />
              }
            />
          )
        },
        {
          id: 'cropSharePercent',
          colSpan: COL_SPAN_ONE_FOURTH,
          render: () => (
            <FormInputWithPrecisionDisplay
              object={stableFieldObject(
                'cropSharePercent',
                values.projectedRevenue.cropSharePercent
              )}
              fieldName="cropSharePercent"
              label="Crop Share %"
              inputSuffix="%"
              disabled={financialFieldsDisabled}
              allowEmptyDisplay
              onControlledBlur={newValue => {
                const cropSharePercent = parseControlledBlurValue(newValue);
                if (cropSharePercent != null && cropSharePercent > 100) {
                  setFieldValue(
                    'projectedRevenue.cropSharePercent',
                    lastAcceptedCropSharePercent.current
                  );
                  return;
                }

                const acceptedCropSharePercent = cropSharePercent ?? 100;
                lastAcceptedCropSharePercent.current = acceptedCropSharePercent;
                setFieldValue(
                  'projectedRevenue.cropSharePercent',
                  acceptedCropSharePercent
                );
              }}
            />
          )
        },
        {
          id: 'projectedNetRevenue',
          colSpan: COL_SPAN_ONE_FOURTH,
          render: () => (
            <ReadOnlyCurrency
              label="Projected Net Revenue"
              value={projectedNetRevenue}
              testId="projectedNetRevenue-readout"
            />
          )
        }
      ]
    }
  ];

  return <FormGrid rows={projectedRevenueRows} />;
};

export default ProjectedRevenueSection;
